import { useState, useCallback } from 'react';
import type { ChatMessage, DiceResult } from '../types';
import type { RollResult } from '@roll1/shared';
import { uuid, timeStr } from '../utils';
import type { UseDiceReturn } from './useDice';
import { useI18n } from '../i18n';

export interface UseChatReturn {
  messages: ChatMessage[];
  playerName: string;
  setPlayerName: (name: string) => void;
  send: (text: string) => void;
  addSystemMessage: (text: string) => void;
  addRollMessage: (author: string, result: DiceResult) => void;
  addSheetRollMessage: (rollResult: RollResult) => void;
  addMessage: (msg: ChatMessage) => void;
  updateMessage: (messageId: string, changes: Partial<ChatMessage>) => void;
  initMessages: (msgs: ChatMessage[]) => void;
  aiTyping: false | 'connecting' | 'thinking' | 'generating';
  setAiTyping: (typing: false | 'connecting' | 'thinking' | 'generating') => void;
}

export function useChat(dice: UseDiceReturn): UseChatReturn {
  const { t } = useI18n();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [playerName, setPlayerName] = useState(t('chat.defaultName'));
  const [aiTyping, setAiTyping] = useState<false | 'connecting' | 'thinking' | 'generating'>(false);

  const addMessageRaw = useCallback((author: string, text: string, type: ChatMessage['type'] = 'text') => {
    const msg: ChatMessage = { id: uuid(), author, text, type, time: timeStr() };
    setMessages(prev => [...prev, msg]);
  }, []);

  const addMessage = useCallback((msg: ChatMessage) => {
    setMessages(prev => [...prev, msg]);
  }, []);

  const updateMessage = useCallback((messageId: string, changes: Partial<ChatMessage>) => {
    setMessages(prev => prev.map(m => m.id === messageId ? { ...m, ...changes } : m));
  }, []);

  const initMessages = useCallback((msgs: ChatMessage[]) => {
    setMessages(msgs);
  }, []);

  const addSystemMessage = useCallback((text: string) => {
    const msg: ChatMessage = { id: uuid(), author: '', text, type: 'system', time: timeStr() };
    setMessages(prev => [...prev, msg]);
  }, []);

  const addRollMessage = useCallback((author: string, result: DiceResult) => {
    const msg: ChatMessage = {
      id: uuid(),
      author,
      text: result.formula,
      type: 'roll',
      result: result.total,
      details: result.details,
      time: result.timestamp,
    };
    setMessages(prev => [...prev, msg]);
  }, []);

  const addSheetRollMessage = useCallback((rollResult: RollResult) => {
    const msg: ChatMessage = {
      id: uuid(),
      author: rollResult.characterName,
      text: rollResult.rollName,
      type: 'sheet-roll',
      time: rollResult.timestamp || timeStr(),
      rollResult,
    };
    setMessages(prev => [...prev, msg]);
  }, []);

  const send = useCallback((text: string) => {
    if (!text.trim()) return;

    if (text.startsWith('/')) {
      const parts = text.trim().split(/\s+/);
      const cmd = parts[0].toLowerCase();

      if (cmd === '/roll' || cmd === '/r') {
        const formula = parts.slice(1).join('');
        if (formula) {
          const result = dice.roll(formula);
          if (result) addRollMessage(playerName, result);
        } else {
          addSystemMessage(t('chat.helpUsage'));
        }
      } else if (cmd === '/name') {
        const newName = parts.slice(1).join(' ') || t('chat.defaultName');
        setPlayerName(newName);
        addSystemMessage(`${t('chat.nameChanged')} ${newName}`);
      } else if (cmd === '/clear') {
        setMessages([]);
      } else if (cmd === '/help') {
        addSystemMessage(t('chat.helpRoll'));
        addSystemMessage(t('chat.helpName'));
        addSystemMessage(t('chat.helpClear'));
        addSystemMessage(t('chat.helpGm'));
      } else if (cmd === '/gm') {
        const msg = parts.slice(1).join(' ');
        addMessageRaw(t('chat.gmPrefix') + playerName, msg, 'gm');
      } else {
        addSystemMessage(t('chat.unknownCommand'));
      }
    } else {
      addMessageRaw(playerName, text);
    }
  }, [playerName, dice, addMessageRaw, addRollMessage, addSystemMessage, t]);

  return { messages, playerName, setPlayerName, send, addSystemMessage, addRollMessage, addSheetRollMessage, addMessage, updateMessage, initMessages, aiTyping, setAiTyping };
}
